import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { policiesKeys } from "../queryKeys/queryKeys";
import { policiesSaveFilterViewApi } from "@repo/api/clients/policiesSaveFilterView.api";
import {
  CreateViewRequestPayload,
  UpdateViewRequestPayload,
} from "@repo/api/schemas/policiesSaveFilterView.schema";
import { useIntl } from "react-intl";

export const useGetSaveViewList = () => {
  return useQuery({
    queryKey: policiesKeys.saveViewList(),
    queryFn: () => policiesSaveFilterViewApi.getSaveViewList(),
    retry: 1,
  });
};

export const useUpdateSaveFilterView = ({
  onError,
  onSuccess,
  onSettled,
}: {
  onError: (message: string) => void;
  onSuccess?: () => void;
  onSettled?: () => void;
}) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateViewRequestPayload) =>
      policiesSaveFilterViewApi.updateSaveFilterView(payload),
    onSuccess: () => {
      // Invalidate the views query to refresh the list
      queryClient.invalidateQueries({
        queryKey: policiesKeys.saveViewList(),
      });
      onSuccess?.();
    },
    onError: () => {
      onError(
        intl.formatMessage({
          id: "zIMsz9",
          defaultMessage: "Failed to update view",
          description: "Error message for view update failure",
        }),
      );
    },
    onSettled: () => {
      onSettled?.();
    },
  });
};

export const useCreateSaveFilterView = ({
  onError,
  onSuccess,
  onSettled,
}: {
  onError?: (message: string) => void;
  onSuccess?: () => void;
  onSettled?: () => void;
} = {}) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateViewRequestPayload) =>
      policiesSaveFilterViewApi.createSaveFilterView(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: policiesKeys.saveViewList(),
      });
      onSuccess?.();
    },
    onError: () => {
      onError?.(
        intl.formatMessage({
          id: "o473bt",
          defaultMessage: "Failed to create view",
          description: "Error message for view create failure",
        }),
      );
    },
    onSettled: () => {
      onSettled?.();
    },
  });
};

export function useDeleteSavedView({
  onError,
  onSuccess,
  onSettled,
}: {
  onError: (message: string) => void;
  onSuccess?: () => void;
  onSettled?: () => void;
}) {
  const intl = useIntl();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (viewId: number) => {
      return await policiesSaveFilterViewApi.deleteSaveFilterView(viewId);
    },
    onSuccess: () => {
      // Invalidate the views query to refresh the list
      queryClient.invalidateQueries({
        queryKey: policiesKeys.saveViewList(),
      });
      onSuccess?.();
    },
    onError: () => {
      onError(
        intl.formatMessage({
          id: "JPLp6+",
          defaultMessage: "Failed to delete view",
          description: "Error message for view delete failure",
        }),
      );
    },
    onSettled: () => {
      onSettled?.();
    },
  });
}
